import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hammer, CheckCheck, MessagesSquare, PenLine, Compass, AlertTriangle, ArrowRight } from "lucide-react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { CATS, useCatColors, fmtDur, fmtClock } from "../bits";
import { Sk, sentenceLines, PointLines, InsightCallout, ExpandChevron } from "./storyBits";
import { ToolIcon } from "../../../components/classic/panels";
import { DrawerSec, DrawerSpine } from "./StoryDrawer";

/* Chapters: every thread of the day as a card, ranked by time. The card is
   deterministic (/day data only); the deep read streams from /thread-detail
   when a chapter is opened in the drawer. */

export const CAT_ICON = {
  deep: Hammer, review: CheckCheck, comms: MessagesSquare,
  docs: PenLine, browse: Compass, distract: AlertTriangle,
};

export function chapterBlurb(th) {
  if (th.outcomes?.length) return th.outcomes[0].text;
  if (th.topics?.length) return `Around ${th.topics.slice(0, 3).join(", ")}.`;
  return `${th.stretches} stretch${th.stretches === 1 ? "" : "es"} through the day in ${th.apps?.slice(0, 2).join(" and ") || "one place"}.`;
}

export function StoryChapters({ threads = [], totals, bounds, loading, onOpen, nested }) {
  const t = useDashTheme();
  const C = useCatColors();
  const [showAllTail, setShowAllTail] = React.useState(false);
  const dayMin = Math.max(1, totals?.trackedMin || 1);
  const t0 = bounds?.startMs, span = Math.max(1, (bounds?.endMs || 0) - (t0 || 0));

  /* A fragmented day can split into dozens of threads. A wall of 30 equal
     cards buries the work that mattered — so the section is tiered: the
     threads that DEFINED the day keep their cards, everything else folds
     into a compact ranked list. Every row still opens the same deep read. */
  const { majors, tail } = React.useMemo(() => {
    const majors = [], tail = [];
    for (const th of threads) {
      const big = (th.minutes / dayMin >= 0.08 || th.minutes >= 45) && majors.length < 6;
      if (big || majors.length < 3) majors.push(th); else tail.push(th);
    }
    return { majors, tail };
  }, [threads, dayMin]);
  const tailMin = tail.reduce((a, th) => a + th.minutes, 0);
  const tailShown = showAllTail ? tail : tail.slice(0, 8);

  if (loading) {
    return (
      <div className="st-chapters" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: t.s[4] }}>
        {[0, 1, 2, 3].map((i) => <Sk key={i} h={172} r={14} />)}
      </div>
    );
  }
  if (!threads.length) return null;

  return (
    <div>
    <div className="st-chapters" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: t.s[4] }}>
      {majors.map((th, i) => {
        const col = C[th.cat] || C.browse;
        const share = Math.min(100, Math.round((th.minutes / dayMin) * 100));
        return (
          <motion.button
            key={th.key} type="button"
            className="st-reset st-chapter st-hit st-focusable"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: t.motion.ease.out, delay: Math.min(i * 0.06, 0.35) }}
            onClick={() => onOpen?.(th)}
            style={{
              "--st-acc": col, cursor: "pointer", position: "relative", overflow: "hidden",
              background: nested ? (t.isDark ? t.c.surfaceMuted : "#F4F6FA") : t.c.surface,
              border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
              boxShadow: nested ? "none" : t.shadow.sm, padding: `${t.s[4]}px ${t.s[5]}px`,
              display: "flex", flexDirection: "column",
            }}
          >
            <span style={{ display: "flex", alignItems: "flex-start", gap: 13, width: "100%" }}>
              {/* The app's own mark. A category glyph told you it was "building";
                  the Cursor icon tells you it was building IN CURSOR, which is
                  what a reader recognises at a glance. Apps outside the brand
                  registry fall back to the category glyph inside ToolIcon. */}
              <ToolIcon name={(th.apps || [])[0] || ""} cat={th.cat} size={42} />
              <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <span style={{
                  display: "block", fontSize: t.font.size.md, fontWeight: t.font.weight.semibold,
                  letterSpacing: "-0.01em", lineHeight: 1.35,
                  overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }} title={th.title}>{th.title}</span>
                <span style={{ display: "block", fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 3 }}>
                  {fmtDur(th.minutes)} · {CATS[th.cat]?.label || th.cat}
                  {th.stretches > 1 ? ` · ${th.stretches} sittings` : ""}
                </span>
              </span>
              <span style={{ flex: "none", textAlign: "right" }}>
                <span style={{ display: "block", fontSize: 19, fontWeight: t.font.weight.semibold, letterSpacing: "-0.02em", color: col, fontVariantNumeric: "tabular-nums" }}>{share}%</span>
                <span style={{ display: "block", fontSize: 10.5, color: t.c.textTertiary, fontWeight: 600 }}>of the day</span>
              </span>
            </span>

            <span style={{
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
              fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.55,
              margin: `${t.s[3]}px 0 ${t.s[3]}px`, textAlign: "left",
            }}>{chapterBlurb(th)}</span>

            {/* when-it-ran strip */}
            <span aria-hidden="true" style={{
              display: "block", position: "relative", height: 7, borderRadius: 99, overflow: "hidden",
              background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover, marginBottom: t.s[3],
            }}>
              {(th.spans || []).map((s, j) => (
                <span key={j} style={{
                  position: "absolute", top: 0, bottom: 0, borderRadius: 99, background: col,
                  left: `${Math.max(0, ((s.startMs - t0) / span) * 100)}%`,
                  width: `${Math.max(1, ((s.endMs - s.startMs) / span) * 100)}%`,
                }} />
              ))}
            </span>

            <span style={{ display: "flex", alignItems: "center", gap: 7, width: "100%" }}>
              {(th.apps || []).slice(0, 3).map((a) => (
                <span key={a} style={{
                  fontSize: 11, fontWeight: t.font.weight.medium, color: t.c.textSecondary,
                  background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
                  borderRadius: t.r.pill, padding: "4px 10px", whiteSpace: "nowrap",
                }}>{a}</span>
              ))}
              <span className="st-go" style={{
                marginLeft: "auto", width: 28, height: 28, borderRadius: "50%", flex: "none",
                border: `1.5px solid ${t.c.border}`, color: t.c.textTertiary,
                display: "grid", placeItems: "center",
              }}><ArrowRight size={13} /></span>
            </span>
          </motion.button>
        );
      })}
    </div>

    {/* ── The long tail — everything shorter, as a dense ranked list ───── */}
    {tail.length > 0 && (
      <div style={{
        marginTop: t.s[4],
        background: nested ? "transparent" : t.c.surface,
        border: nested ? `1px solid ${t.c.border}` : `1px solid ${t.c.border}`,
        borderRadius: t.r.md, boxShadow: nested ? "none" : t.shadow.sm,
        padding: `${t.s[3]}px ${t.s[4]}px`,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold }}>
            The rest of the day
          </span>
          <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
            {tail.length} shorter thread{tail.length === 1 ? "" : "s"} · {fmtDur(tailMin)} combined · every row opens its deep read
          </span>
        </div>
        {tailShown.map((th, i) => {
          const col = C[th.cat] || C.browse;
          const share = Math.max(1, Math.round((th.minutes / dayMin) * 100));
          return (
            <button
              key={th.key} type="button"
              className="st-reset st-approw st-focusable"
              onClick={() => onOpen?.(th)}
              style={{
                display: "flex", alignItems: "center", gap: 11, width: "100%",
                padding: "8px 6px", borderRadius: 9, cursor: "pointer",
                borderTop: i ? `1px solid ${t.c.border}` : "none",
              }}
            >
              <ToolIcon name={(th.apps || [])[0] || ""} cat={th.cat} size={20} />
              <span style={{
                flex: 1, minWidth: 0, textAlign: "left",
                fontSize: t.font.size.sm, fontWeight: t.font.weight.medium,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={th.title}>{th.title}</span>
              <span style={{ flex: "none", fontSize: t.font.size.xs, color: t.c.textTertiary, whiteSpace: "nowrap" }}>
                {CATS[th.cat]?.label || th.cat}{th.stretches > 1 ? ` · ${th.stretches}×` : ""}
              </span>
              <span style={{ flex: "none", width: 52, textAlign: "right", fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, fontVariantNumeric: "tabular-nums" }}>
                {fmtDur(th.minutes)}
              </span>
              {/* The colour dot moved into the app icon, so the category signal
                  rides on the share figure instead of disappearing. */}
              <span style={{ flex: "none", width: 30, textAlign: "right", fontSize: t.font.size.xs, color: col, fontWeight: t.font.weight.semibold, fontVariantNumeric: "tabular-nums" }}>
                {share}%
              </span>
              <ArrowRight size={12} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
            </button>
          );
        })}
        {tail.length > 8 && (
          <button
            type="button"
            className="st-reset st-focusable st-textlink"
            onClick={() => setShowAllTail((v) => !v)}
            style={{
              display: "block", width: "100%", textAlign: "center", cursor: "pointer",
              padding: "9px 0 3px", fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold,
              color: t.c.ai, borderTop: `1px solid ${t.c.border}`, marginTop: 4,
            }}
          >
            {showAllTail ? "Show fewer" : `Show all ${tail.length} threads`}
          </button>
        )}
      </div>
    )}
    </div>
  );
}

/* ── Drawer body: the deep read of one thread ──────────────────────────── */

export function ThreadDeepRead({ thread, bounds, userId, date, source }) {
  const t = useDashTheme();
  const tone = t.c.primary;
  const [detail, setDetail] = React.useState(null);
  const [story, setStory] = React.useState(null);   // {stages:[{stage,points}], wrap} | {text}
  const [openIdx, setOpenIdx] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [streaming, setStreaming] = React.useState(true);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) return undefined;   // one call per open
    startedRef.current = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/thread-detail`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId, date, source, threadKey: thread.key }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
          throw new Error(err?.message || "Could not open this thread.");
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
            if (d.type === "stretches") setDetail({ stretches: d.stretches, topics: d.topics, outcomes: [] });
            else if (d.type === "analysis") {
              setDetail((cur) => {
                if (!cur?.stretches?.[d.index]) return cur;
                const next = cur.stretches.slice();
                // Merge EVERY layer the analysis carries. Dropping fields here
                // is what made an analysed session read as "nothing recorded".
                next[d.index] = {
                  ...next[d.index],
                  headline: d.headline || next[d.index].headline,
                  lead: d.lead || "",
                  points: Array.isArray(d.points) ? d.points : [],
                  insight: d.insight || "",
                  detail: d.detail || "",
                  items: d.items?.length ? d.items : next[d.index].items,
                  analysed: true,
                };
                return { ...cur, stretches: next };
              });
            } else if (d.type === "narrative") {
              // Stages stream one by one; older cached runs may carry the
              // whole story (stages[]) or prose (text) in a single event.
              setStory((cur) => {
                if (d.stages?.length) return { stages: d.stages, wrap: d.wrap || "" };
                if (d.text) return { text: d.text };
                const base = cur && cur.stages ? cur : { stages: [], wrap: "" };
                if (d.stage) return { ...base, stages: [...base.stages, d.stage] };
                if (d.wrap) return { ...base, wrap: d.wrap };
                return cur;
              });
            }
            else if (d.type === "outcomes") setDetail((cur) => (cur ? { ...cur, outcomes: d.outcomes } : cur));
            else if (d.type === "error") setError(d.message);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Could not open this thread.");
      } finally {
        setStreaming(false);
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Until the stream's timeline lands, the deterministic list from /day
  // stands in — the drawer is never empty.
  const rows = detail?.stretches?.length
    ? detail.stretches
    : (thread.sessionsList || []).map((s) => ({
      startMs: s.startMs, endMs: s.endMs, minutes: s.minutes, app: s.app,
      headline: s.title || s.app, activityLabel: s.activityLabel,
    }));

  return (
    <div>
      <DrawerSec title="When it ran">
        <DrawerSpine
          spans={thread.spans || []} bounds={bounds} tone={tone}
          startLabel={bounds ? fmtClock(bounds.startMs) : ""} endLabel={bounds ? fmtClock(bounds.endMs) : ""}
        />
      </DrawerSec>

      {(thread.topics?.length || thread.artifacts?.length) ? (
        <DrawerSec title="What it was about">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {(thread.topics || []).slice(0, 6).map((x) => (
              <span key={x} style={{
                fontSize: 12, fontWeight: 600, color: t.c.textSecondary, background: t.c.surfaceMuted,
                border: `1px solid ${t.c.border}`, borderRadius: t.r.pill, padding: "4px 10px",
              }}>{x}</span>
            ))}
            {(thread.artifacts || []).slice(0, 5).map((a) => (
              <span key={a.name} style={{
                fontSize: 11, color: t.c.textSecondary, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
                borderRadius: t.r.sm, padding: "4px 9px",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={a.name}>{a.name}</span>
            ))}
          </div>
        </DrawerSec>
      ) : null}

      {/* The full story — structured stages on a timeline rail, never prose.
          Old cached runs that streamed a prose narrative are converted here:
          each paragraph becomes a stage of sentence lines. */}
      {story && (story.stages?.length || story.text) ? (
        <DrawerSec title="The full story">
          {(() => {
            const stages = story.stages?.length
              ? story.stages
              : (story.text || "").split(/\n{2,}/).map((p, i) => ({
                stage: `Part ${i + 1}`, points: sentenceLines(p),
              })).filter((s) => s.points.length);
            const writing = streaming && !story.wrap;
            return (
              <div>
                {stages.map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    style={{ position: "relative", paddingLeft: 28, paddingBottom: (i < stages.length - 1 || writing) ? 24 : 0 }}
                  >
                    {/* timeline rail + node */}
                    {(i < stages.length - 1 || writing) && (
                      <span aria-hidden="true" style={{
                        position: "absolute", left: 6.5, top: 20, bottom: -4, width: 1.5,
                        background: t.c.border,
                      }} />
                    )}
                    <span aria-hidden="true" style={{
                      position: "absolute", left: 0, top: 5, width: 14, height: 14, borderRadius: "50%",
                      background: t.c.primarySoft, border: `2px solid ${t.c.primary}`,
                    }} />
                    <span style={{
                      display: "block", fontSize: t.font.size.lg, fontWeight: t.font.weight.semibold,
                      letterSpacing: "-0.008em", marginBottom: 10,
                    }}>{s.stage}</span>
                    <PointLines points={s.points} tone={tone} />
                  </motion.div>
                ))}
                {/* the next stage, being written */}
                {writing && (
                  <div style={{ position: "relative", paddingLeft: 28 }}>
                    <span aria-hidden="true" style={{
                      position: "absolute", left: 3, top: 6, width: 9, height: 9, borderRadius: "50%",
                      background: tone, opacity: 0.5,
                    }} />
                    <Sk w="42%" h={12} style={{ marginBottom: 7 }} />
                    <Sk w="78%" h={11} />
                  </div>
                )}
                {story.wrap ? <InsightCallout text={story.wrap} tone={tone} /> : null}
              </div>
            );
          })()}
        </DrawerSec>
      ) : streaming ? (
        <DrawerSec title="The full story">
          <Sk h={13} w="94%" style={{ marginBottom: 9 }} />
          <Sk h={13} w="64%" />
        </DrawerSec>
      ) : null}

      <DrawerSec title="Session by session · open one for its full read">
        {rows.map((s, i) => {
          const isOpen = openIdx === i;
          const hasDepth = !!(s.points?.length || s.insight || s.detail || s.lead);
          const points = s.points?.length ? s.points : sentenceLines(s.detail);
          return (
            <div key={i} style={{ borderBottom: i < rows.length - 1 ? `1px solid ${t.c.border}` : "none" }}>
              {/* Compact, scannable row — the detail layer opens on demand. */}
              <button
                type="button"
                className="st-reset st-focusable"
                onClick={() => setOpenIdx(isOpen ? null : i)}
                aria-expanded={isOpen}
                style={{
                  display: "flex", gap: 12, alignItems: "center", width: "100%",
                  padding: "11px 2px", cursor: "pointer",
                }}
              >
                <span style={{ flex: "none", width: 112, fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums", textAlign: "left" }}>
                  {fmtClock(s.startMs)} – {fmtClock(s.endMs)}
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <span style={{
                    display: "block", fontSize: t.font.size.base, fontWeight: t.font.weight.semibold, lineHeight: 1.45,
                    overflow: isOpen ? "visible" : "hidden",
                    textOverflow: "ellipsis", whiteSpace: isOpen ? "normal" : "nowrap",
                  }}>
                    {s.headline || s.app}
                  </span>
                  <span style={{ display: "block", fontSize: t.font.size.md, color: t.c.textTertiary, marginTop: 2 }}>
                    {s.app}{s.minutes ? ` · ${fmtDur(s.minutes)}` : ""}{s.activityLabel ? ` · ${s.activityLabel} activity` : ""}
                    {!hasDepth && streaming ? " · analysing…" : ""}
                  </span>
                </span>
                <ExpandChevron open={isOpen} tone={tone} />
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="body"
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    style={{ overflow: "hidden" }}
                  >
                    <div style={{ padding: "2px 2px 14px 124px" }}>
                      {s.lead && (
                        <span style={{ display: "block", fontSize: t.font.size.base, color: t.c.text, lineHeight: 1.55, marginBottom: points.length ? 8 : 0 }}>
                          {s.lead}
                        </span>
                      )}
                      {points.length > 0 ? (
                        <PointLines points={points} tone={tone} />
                      ) : hasDepth ? null : streaming ? (
                        <span style={{ display: "block" }}>
                          <Sk w="86%" h={11} style={{ marginBottom: 7 }} /><Sk w="64%" h={11} />
                        </span>
                      ) : (
                        <span style={{ fontSize: t.font.size.base, color: t.c.textTertiary }}>
                          No deeper read for this session — too little was recorded in it.
                        </span>
                      )}
                      <InsightCallout text={s.insight} tone={tone} />
                      {s.items?.length > 0 && (
                        <span style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                          {s.items.map((x) => (
                            <span key={x} style={{
                              fontSize: 10.5, color: t.c.textSecondary, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
                              borderRadius: t.r.sm, padding: "3px 8px",
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                              maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }} title={x}>{x}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
        {streaming && detail?.stretches?.length ? (
          <div style={{ fontSize: t.font.size.md, color: t.c.textTertiary, marginTop: 10 }}>
            Writing the deeper read…
          </div>
        ) : null}
      </DrawerSec>

      {thread.interruptions?.length ? (
        <DrawerSec title="What pulled them away">
          {thread.interruptions.slice(0, 4).map((g, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "7px 0" }}>
              <span style={{ flex: "none", width: 74, fontSize: t.font.size.md, fontWeight: 600, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                {fmtClock(g.afterMs)}
              </span>
              <span style={{ fontSize: t.font.size.base, color: t.c.textSecondary, lineHeight: 1.5 }}>
                {fmtDur(g.gapMin)} gap — {g.cause}
              </span>
            </div>
          ))}
        </DrawerSec>
      ) : null}

      {detail?.outcomes?.length ? (
        <DrawerSec title="What came out of it">
          {detail.outcomes.map((o, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 0" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone, flex: "none", marginTop: 7 }} />
              <span style={{ fontSize: t.font.size.base, color: t.c.textSecondary, lineHeight: 1.55 }}>{o.text || o}</span>
            </div>
          ))}
        </DrawerSec>
      ) : null}

      {error && (
        <div style={{
          fontSize: t.font.size.base, color: t.status.negative, background: t.status.negativeSoft,
          padding: "10px 13px", borderRadius: t.r.sm,
        }}>{error}</div>
      )}
    </div>
  );
}
